import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { Wine } from 'lucide-react';

interface AgeGateProps {
  isOpen: boolean;
  onConfirm: () => void;
  onDeny: () => void;
}

const AgeGate = ({ isOpen, onConfirm, onDeny }: AgeGateProps) => {
  const { t, language } = useLanguage();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
        >
          {/* Background with Blur and Image Mosaics */}
          <div className="absolute inset-0 flex">
            <div className="relative h-full w-1/2 overflow-hidden bg-[#1a0f0f]">
              <img src="/ui/age-gate/age-gate-1.webp" alt="Wine Details" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-black/20" />
            </div>
            <div className="relative h-full w-1/2 overflow-hidden bg-[#1a0f0f]">
              <img src="/ui/age-gate/age-gate-2.webp" alt="Wine Details" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-black/20" />
            </div>
            {/* Primary overlay to darken the entire background for readability */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-[#1a0f0f] z-10" />
          </div>

          {/* Modal */}
          <motion.div
            initial={{ scale: 0.98, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.98, opacity: 0, y: 10 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="relative z-20 mx-4 w-full max-w-lg overflow-hidden rounded-3xl border border-white/20 bg-black/80 p-[1px] shadow-2xl max-h-[95vh] overflow-y-auto"
          >
            <div className="rounded-[23px] bg-gradient-to-b from-white/10 to-black/60 px-4 sm:px-8 py-12 text-center shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)] md:px-12">

              {/* Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring' }}
                className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-full border border-primary/20 bg-primary/10 shadow-[0_0_30px_-5px_rgba(var(--primary-rgb),0.3)]"
              >
                <Wine className="h-10 w-10 text-primary drop-shadow-md" />
              </motion.div>

              {/* Brand */}
              <h1 className="mb-2 font-display text-2xl sm:text-3xl md:text-4xl font-bold tracking-[0.1em] sm:tracking-[0.2em] text-white">
                Włoska piwnica
              </h1>
              <p className="mb-8 text-xs font-medium uppercase tracking-widest text-primary/80">
                Premium Store
              </p>

              {/* Divider */}
              <div className="mx-auto mb-8 h-px w-16 bg-gradient-to-r from-transparent via-white/20 to-transparent" />

              {/* Question */}
              <h2 className="mb-4 font-display text-2xl font-normal text-white">
                {language === 'PL' ? 'Czy masz ukończone 18 lat?' : 'Are you 18 years or older?'}
              </h2>

              <p className="mb-10 text-sm leading-relaxed text-neutral-400">
                {language === 'PL'
                  ? 'Ta strona zawiera treści przeznaczone wyłącznie dla osób pełnoletnich. Potwierdź swój wiek, aby wejść.'
                  : 'This site contains content intended only for adults. Please confirm your age to enter.'}
              </p>

              {/* Buttons */}
              <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
                <motion.button
                  whileHover={{ scale: 1.02, boxShadow: '0 0 20px -5px rgba(220, 38, 38, 0.4)' }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onConfirm}
                  className="group relative overflow-hidden rounded-full bg-primary px-8 py-4 text-sm font-bold uppercase tracking-widest text-white shadow-lg transition-all"
                >
                  <span className="relative z-10">{t('ageGateConfirm')}</span>
                  <div className="absolute inset-0 -translate-x-[100%] bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-[100%]" />
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02, backgroundColor: 'rgba(255,255,255,0.05)' }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onDeny}
                  className="rounded-full border border-white/10 px-8 py-4 text-sm font-bold uppercase tracking-widest text-neutral-400 transition-colors hover:text-white"
                >
                  {t('ageGateDeny')}
                </motion.button>
              </div>

              {/* Footer */}
              <p className="mt-10 text-[10px] text-neutral-500">
                {language === 'PL'
                  ? 'Korzystając ze strony, akceptujesz naszą politykę prywatności i pliki cookies.'
                  : 'By using this site, you accept our privacy policy and cookies.'}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AgeGate;
